import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import { useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";

const BannerMessage = ({
  open,
  message,
  type = "info",
  onClose,
  onRetry,
  autoHideDuration = 2000,
  anchorOrigin = { vertical: "top", horizontal: "center" },
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    if (onClose) {
      onClose(event, reason);
    }
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={handleClose}
      anchorOrigin={anchorOrigin}
      sx={{
        width: { xs: "calc(100% - 16px)", sm: "80%", md: "60%", lg: "50%" },
        minWidth: { xs: 0, sm: 320 },
        maxWidth: "700px",
        left: "50%",
        transform: "translateX(-50%)",
      }}
    >
      <Alert
        variant={isDarkMode ? "filled" : "standard"}
        severity={type}
        sx={{ 
          width: "100%",
          fontWeight: 500,
          boxShadow: 3,
          fontSize: { xs: "0.95rem", sm: "1rem" },
          alignItems: "center",
          "& .MuiAlert-icon": {
            py: 0,
            alignItems: "center",
          },
          "& .MuiAlert-message": {
            display: "flex",
            alignItems: "center",
            py: { xs: 0.25, sm: 0.5 },
            lineHeight: 1.3,
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          },
          "& .MuiAlert-action": {
            display: "flex",
            alignItems: "center",
            pl: 1,
            pr: 0.25,
            py: 0.25,
          },
        }}
        action={
          <>
            {type === "error" && onRetry && (
              <Button
                size="small"
                color="inherit"
                startIcon={<RefreshIcon />}
                onClick={onRetry}
                sx={{ mr: 0.5, fontSize: { xs: "0.75rem", sm: "0.8125rem" } }}
              >
                Retry
              </Button>
            )}
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={handleClose}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </>
        }
      >
        {message}
      </Alert>
    </Snackbar>
  );
};

export default BannerMessage;

BannerMessage.propTypes = {
  open: PropTypes.bool.isRequired,
  message: PropTypes.string,
  type: PropTypes.oneOf(["info", "success", "error", "warning"]),
  onClose: PropTypes.func,
  onRetry: PropTypes.func,
  autoHideDuration: PropTypes.number,
  anchorOrigin: PropTypes.shape({
    vertical: PropTypes.oneOf(["top", "bottom"]),
    horizontal: PropTypes.oneOf(["left", "center", "right"]),
  }),
};
